import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { clinicaProfissionais } from '../../shared/data/clinicaData';
import { Home, Calendar, Activity, User, Bell, ChevronRight, FileText, Pill } from 'lucide-react';

export const HealthApp: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'inicio' | 'agenda' | 'exames' | 'perfil'>('inicio');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans">
      <div className="w-[390px] h-[844px] bg-white text-slate-900 rounded-[3rem] shadow-2xl relative overflow-hidden border-[12px] border-slate-800">
        
        {/* StatusBar */}
        <div className="h-12 flex items-center justify-between px-8 text-xs font-medium pt-2 text-slate-900">
           <span>9:41</span>
           <div className="flex gap-1.5 items-center">
              <div className="w-4 h-3 bg-current rounded-sm"></div>
              <div className="w-3 h-3 bg-current rounded-full"></div>
           </div>
        </div>

        {/* Header App */}
        <div className="px-6 pt-4 pb-4">
           <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                 <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[var(--ym-prospect-primary)] p-0.5">
                    <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format&fit=crop" className="w-full h-full rounded-full object-cover" alt="Patient" />
                 </div>
                 <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-0.5">Olá, Mariana</p>
                    <h1 className="font-heading font-black text-xl text-slate-900">Sua Saúde</h1>
                 </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-600 relative">
                 <Bell className="w-5 h-5" />
                 <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[var(--ym-prospect-accent)]"></div>
              </div>
           </div>
        </div>

        {/* Content Area */}
        <div className="px-6 h-full overflow-y-auto pb-32 ym-hide-scrollbar">
           
           {/* Card Próxima Consulta */}
           <h3 className="font-heading font-bold text-lg mb-4 text-slate-900">Próximo Compromisso</h3>
           <div className="bg-gradient-to-br from-[var(--ym-prospect-primary)] to-[var(--ym-prospect-accent)] text-white p-5 rounded-[2rem] mb-8 shadow-xl shadow-[var(--ym-prospect-primary)]/20 relative overflow-hidden">
              <div className="absolute -right-4 -top-4 opacity-10"><Activity className="w-32 h-32" /></div>
              
              <div className="flex justify-between items-start mb-6 relative z-10">
                 <div className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-bold">Hoje, 14:30</div>
                 <div className="w-8 h-8 rounded-full bg-white text-[var(--ym-prospect-primary)] flex items-center justify-center"><Calendar className="w-4 h-4" /></div>
              </div>

              <div className="flex items-center gap-4 relative z-10">
                 <img src={clinicaProfissionais[0].photo} className="w-12 h-12 rounded-xl object-cover border-2 border-white/20" alt="Doctor" />
                 <div>
                    <h4 className="font-bold">{clinicaProfissionais[0].name}</h4>
                    <p className="text-xs text-white/80">{clinicaProfissionais[0].specialty}</p>
                 </div>
              </div>
           </div>

           {/* Ações Rápidas */}
           <div className="flex gap-4 mb-8">
              <button className="flex-1 bg-slate-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
                 <Calendar className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                 <span className="text-xs font-bold text-slate-600">Agendar</span>
              </button>
              <button className="flex-1 bg-slate-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
                 <FileText className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                 <span className="text-xs font-bold text-slate-600">Exames</span>
              </button>
              <button className="flex-1 bg-slate-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
                 <Pill className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                 <span className="text-xs font-bold text-slate-600">Receitas</span>
              </button>
           </div>

           {/* Documentos Recentes */}
           <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-bold text-lg text-slate-900">Últimos Laudos</h3>
              <button className="text-xs font-bold text-[var(--ym-prospect-primary)]">Ver todos</button>
           </div>
           
           <div className="space-y-3">
              <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                 <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center">
                       <FileText className="w-5 h-5" />
                    </div>
                    <div>
                       <h4 className="text-sm font-bold text-slate-900">Hemograma Completo</h4>
                       <p className="text-[10px] text-slate-500">Realizado em 10/10/2026</p>
                    </div>
                 </div>
                 <ChevronRight className="w-5 h-5 text-slate-300" />
              </div>
              <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                 <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center">
                       <Pill className="w-5 h-5" />
                    </div>
                    <div>
                       <h4 className="text-sm font-bold text-slate-900">Prescrição - Dr. Roberto</h4>
                       <p className="text-[10px] text-slate-500">Válida até 10/11/2026</p>
                    </div>
                 </div>
                 <ChevronRight className="w-5 h-5 text-slate-300" />
              </div>
           </div>

        </div>

        {/* Bottom Nav */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-white/90 backdrop-blur-xl border-t border-slate-100 flex justify-around items-center px-6 pb-6 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)]">
           <button onClick={() => setActiveTab('inicio')} className={`flex flex-col items-center gap-1 ${activeTab === 'inicio' ? 'text-[var(--ym-prospect-primary)]' : 'text-slate-400'}`}>
              <Home className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Início</span>
           </button>
           <button onClick={() => setActiveTab('agenda')} className={`flex flex-col items-center gap-1 ${activeTab === 'agenda' ? 'text-[var(--ym-prospect-primary)]' : 'text-slate-400'}`}>
              <Calendar className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Agenda</span>
           </button>
           <button onClick={() => setActiveTab('exames')} className={`flex flex-col items-center gap-1 ${activeTab === 'exames' ? 'text-[var(--ym-prospect-primary)]' : 'text-slate-400'}`}>
              <Activity className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Exames</span>
           </button>
           <button onClick={() => setActiveTab('perfil')} className={`flex flex-col items-center gap-1 ${activeTab === 'perfil' ? 'text-[var(--ym-prospect-primary)]' : 'text-slate-400'}`}>
              <User className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Perfil</span>
           </button>
        </div>

      </div>
      
      <div className="absolute top-10 right-10 text-slate-500 text-sm max-w-xs bg-white p-4 rounded-xl shadow-xl border border-slate-100">
        <p className="font-bold mb-2 text-slate-900">Conceito de App</p>
        <p>Aplicativo voltado para o paciente: Retenção, agendamento de retornos e visualização de resultados de forma unificada.</p>
      </div>
    </div>
  );
};
